// src/components/dashboard/SupportPathways.jsx
//
// The member-facing Help & Support Pathways (data/supportPathways.js): who a
// member can talk to, words to start with, and what happens next. Lives in the
// Talk-to-Someone modal between the org's own outlets and the crisis lines, so
// a member has three doors in one place — a person they know, a line, and the
// words to open either.

import React, { useState } from 'react';
import { MessageCircle, ShieldAlert, Users, ChevronDown, Quote, ExternalLink } from 'lucide-react';

const ICONS = { MessageCircle, ShieldAlert, Users };

const Section = ({ section }) => {
    const [showStarters, setShowStarters] = useState(false);
    const Icon = ICONS[section.icon] || MessageCircle;
    const starters = Array.isArray(section.starters) ? section.starters : [];
    const people = Array.isArray(section.people) ? section.people : [];
    const routes = Array.isArray(section.routes) ? section.routes : [];
    const outro = Array.isArray(section.outro) ? section.outro : [];

    return (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-brand-light text-brand-primary flex items-center justify-center shrink-0"><Icon size={16} /></div>
                <div className="min-w-0">
                    <p className="text-sm font-black text-slate-900 leading-snug">{section.title}</p>
                    {section.lead && <p className="text-sm text-slate-700 font-semibold leading-relaxed mt-1">{section.lead}</p>}
                </div>
            </div>

            {section.intro && <p className="text-sm text-slate-600 font-medium leading-relaxed mt-3">{section.intro}</p>}

            {people.length > 0 && (
                <ul className="mt-2 space-y-1">
                    {people.map((p, i) => (
                        <li key={i} className="text-sm text-slate-700 font-medium flex gap-2"><span className="text-brand-primary shrink-0">•</span><span>{p}</span></li>
                    ))}
                </ul>
            )}

            {routes.length > 0 && (
                <div className="mt-2 space-y-2">
                    {routes.map((r, i) => (
                        <div key={i} className="bg-white rounded-xl border border-slate-200 px-3 py-2">
                            {r.href
                                ? <a href={r.href} target="_blank" rel="noopener noreferrer" className="text-sm font-black text-brand-primary hover:underline inline-flex items-center gap-1">{r.label} <ExternalLink size={12} /></a>
                                : <p className="text-sm font-black text-slate-800">{r.label}</p>}
                            <p className="text-xs text-slate-500 font-medium leading-relaxed">{r.text}</p>
                        </div>
                    ))}
                </div>
            )}

            {starters.length > 0 && (
                <div className="mt-3">
                    <button
                        type="button"
                        onClick={() => setShowStarters(s => !s)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-left text-sm font-black text-brand-primary hover:bg-brand-light/40 transition-colors"
                    >
                        <span>{section.startersTitle || 'Not sure what to say?'}</span>
                        <ChevronDown size={16} className={`shrink-0 transition-transform ${showStarters ? 'rotate-180' : ''}`} />
                    </button>
                    {showStarters && (
                        <div className="mt-2 space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                            {starters.map((s, i) => (
                                <p key={i} className="text-sm text-slate-700 font-medium leading-snug flex gap-2 bg-white rounded-xl border border-slate-100 px-3 py-2">
                                    <Quote size={13} className="text-brand-primary shrink-0 mt-0.5" />
                                    <span>{s}</span>
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {outro.map((o, i) => (
                <p key={i} className="text-xs text-slate-500 font-medium leading-relaxed mt-2">{o}</p>
            ))}
        </div>
    );
};

const SupportPathways = ({ sections = [] }) => {
    if (!Array.isArray(sections) || sections.length === 0) return null;
    return (
        <div className="space-y-3">
            {sections.map(s => <Section key={s.id} section={s} />)}
        </div>
    );
};

export default SupportPathways;
